import { ButtonBase } from '@material-ui/core'
import React, { FC, Fragment, ReactElement, useCallback, useContext, useMemo } from 'react'
import { AppMenu as AppMenuLanguage } from '../../../../../../i18n/index'
import { MainMenuApp } from '../../../../../NonReact/constants/AppMenuConstants'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { ArrayUtils } from '../../../../../NonReact/utils/ArrayUtils'
import { isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import Icon, { IconName } from '../../../../../React/Common/Components/Icon'
import IconButton, { IconButtonType } from '../../../../../React/Common/Components/IconButton'
import { LanguageContext, MessageContext } from '../../../../../React/Common/Contexts'
import { RxJSCurrentState, useRxJSState } from '../../../../../React/Common/Hooks/useRxJSState'
import { reportingMenuFavourites$ } from '../../../../../NonReact/common/RxJS/ReportingMenuFavouritesState'
import { subMenuItemsState$ } from '../../../../../NonReact/common/RxJS/SubMenuItemsState'
import { AppMenuEntry } from '../Types/Types'
import { MessageContextType } from '../../../../../React/Common/Contexts/MessageContext'
import { SubMenuItem } from '../../../../../NonReact/common/Types/Types'
import { SidebarMenuItems } from '../../../../../NonReact/constants/ReactRouter/Header/Constants'
import { updateUserFavourites } from '../../../../../NonReact/common/API/EntityAPIClient'

type Props = {
    appMenuState: RxJSCurrentState<MainMenuApp>
    appMenuEntries: Definable<AppMenuEntry[]>
    closeMenu: () => void
}

export const AppMenu: FC<Props> = ({
    appMenuState: [appMenuValue, setAppMenuValue],
    appMenuEntries,
    closeMenu
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const { displayError }: MessageContextType = useContext(MessageContext)

    const [subMenuItems]: RxJSCurrentState<Record<MainMenuApp, SubMenuItem[]>> = useRxJSState(subMenuItemsState$)
    const favouriteSubMenuItems: SubMenuItem[] = useMemo(
        (): SubMenuItem[] =>
            Object.values(subMenuItems ?? {}).flatMap((subMenuItemsEntry: SubMenuItem[]): SubMenuItem[] =>
                subMenuItemsEntry.filter((subMenuItem: SubMenuItem): boolean => !!subMenuItem?.favourite)
            ),
        [subMenuItems]
    )
    const [favourites, setFavourites]: RxJSCurrentState<SidebarMenuItems[]> =
        useRxJSState<SidebarMenuItems[]>(reportingMenuFavourites$)

    const handleOnClickFavourite = useCallback(
        (id: SidebarMenuItems): void => {
            const filteredFavourites: SidebarMenuItems[] = favourites?.includes(id)
                ? favourites.filter((fav: SidebarMenuItems): boolean => fav !== id)
                : [...(favourites ?? []), id]

            setFavourites(filteredFavourites)
            updateUserFavourites(filteredFavourites).catch(displayError)
        },
        [favourites]
    )

    return (
        <div className='app-menu'>
            <div className='am-menu-entries'>
                <h3 className='am-title'>{language.get(AppMenuLanguage.AppMenu_AppsTitle)}</h3>
                <div className='amme-entries'>
                    {appMenuEntries
                        ?.filter((appMenuEntry: AppMenuEntry): boolean => appMenuEntry.enabled)
                        ?.map(({ menu, icon, title }: AppMenuEntry, index: number): ReactElement => {
                            let isActive: boolean = false

                            if (isVarNotEmpty(appMenuValue)) {
                                isActive = appMenuValue === menu
                            } else if (index === 0) {
                                isActive = true
                            }

                            return (
                                <div
                                    className={`amme-entry ${isActive ? 'ammee-active' : EMPTY_STRING}`}
                                    key={`${icon}-entry`}
                                >
                                    <ButtonBase
                                        className='ammee-content'
                                        onClick={(): void => {
                                            setAppMenuValue(menu)
                                            closeMenu()
                                        }}
                                    >
                                        <Icon iconName={icon} />
                                        <span className='ammeec-name'>{title}</span>
                                    </ButtonBase>
                                </div>
                            )
                        })}
                </div>
            </div>
            {!ArrayUtils.isEmpty(favouriteSubMenuItems) ? (
                <div className='am-menu-entries am-favourites'>
                    <h3 className='am-title'>{language.get(AppMenuLanguage.AppMenu_FavouritesTitle)}</h3>
                    <div className='amme-entries'>
                        {favouriteSubMenuItems.map(
                            ({
                                id,
                                title,
                                handleClick,
                                icon,
                                active,
                                enabled,
                                favourite
                            }: SubMenuItem): ReactElement =>
                                enabled ? (
                                    <div
                                        className={`amme-entry ${active ? 'ammee-active' : EMPTY_STRING}`}
                                        key={`${icon}-entry`}
                                    >
                                        <ButtonBase
                                            className='ammee-content'
                                            onClick={(): void => {
                                                handleClick()
                                                closeMenu()
                                            }}
                                        >
                                            <Icon iconName={icon} />
                                            <span className='ammeec-name'>{title}</span>
                                        </ButtonBase>
                                        <IconButton
                                            customClassName='ammeec-favourite'
                                            type={IconButtonType.BUTTON}
                                            iconName={favourite ? IconName.FAVOURITE : IconName.FAVOURITE_OUTLINED}
                                            onClick={(): void => handleOnClickFavourite(id)}
                                        />
                                    </div>
                                ) : (
                                    <Fragment key={icon} />
                                )
                        )}
                    </div>
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
